
import { Commit, Dispatch } from 'vuex';
import { ListStoreBase } from '@/main/store/@types/list.d';
import { updateFilterAction, updateTableDataAction, updateTableSchemaAction, commitRowDetail } from '@/main/store/actionsFunctions';
import {
	FILTER,
	TABLE_DATA,
	TABLE_SCHEMA,
	ROW_DETAIL, TABLE_DATA_EXPORT,
} from '@/main/store/listEditTypes';
import { BaseApi } from '@/main/baseApi';
import { columnsToCamelCase, convertToObjects, getColumnsRowMapObject, getColumnsRowMapObjectForExportFunc, ObjectType } from '@/main/utils/table';
import { forceFileDownload, parseObj2Csv, renameKeys, localize, localizeFileName, renameKeysForExportFunc } from '@/main/utils/fileHelper';
import { date } from '@storybook/addon-knobs';

export function listActions(apiProvider: BaseApi) {
	return {
		/**
		 * Generates an action that commits FILTER mutation.
		 * Calls `apiProvider[isSelect ? 'filterSelect' : 'filter']`.
		 *
		 * Updates data in dropdownList and Filter in Vuex store using the
		 * {@link updateFilterAction} helper function.
		 *
		 * @returns this.
		 */
		[FILTER](
			{ state, commit, dispatch }: { state: ListStoreBase, commit: Commit, dispatch: Dispatch },
			isSelect = false,
		) {
			return updateFilterAction(state, commit, dispatch,
				apiProvider[isSelect ? 'filterSelect' : 'filter']({ cancelTokenKey: FILTER, commit }));
		},

		/**
		 * Generates an action that commits TABLE_SCHEMA mutation.
		 * Calls `apiProvider[isSelect ? 'tableSchemaSelect' : 'tableSchema']`.
		 *
		 * Updates table schema using the {@link updateTableSchemaAction} helper function.
		 *
		 * @returns this.
		 */
		async [TABLE_SCHEMA](
			{ state, commit, dispatch }: { state: ListStoreBase, commit: Commit, dispatch: Dispatch },
			{ isSelect = false }: { isSelect: boolean },
		) {
			return updateTableSchemaAction(state, commit, dispatch,
				apiProvider[isSelect ? 'tableSchemaSelect' : 'tableSchema'](
					{ cancelTokenKey: TABLE_SCHEMA, commit },
				),
				'table');
		},

		/**
		 * Generates an action that commits TABLE_DATA mutation.
		 * Calls `apiProvider[isSelect ? 'tableDataSelect' : 'tableData']`.
		 *
		 * Updates table data using the {@link updateTableDataAction} helper function.
		 *
		 * @returns this.
		 */
		[TABLE_DATA](
			{ state, commit, dispatch }: { state: ListStoreBase, commit: Commit, dispatch: Dispatch },
			{ isSelect = false },
		) {
			return updateTableDataAction(
				state,
				commit,
				dispatch,
				apiProvider[isSelect ? 'tableDataSelect' : 'tableData'](
					state.filter, { cancelTokenKey: TABLE_SCHEMA, commit },
				),
				'table',
			);
		},

		[TABLE_DATA_EXPORT]({ state, commit, dispatch }: { state: ListStoreBase, commit: Commit, dispatch: Dispatch }) {
			const tableData = [] as any;
			let id: any;
			let rebateId: any;
			if (state.name === 'LabourSalesList') {
				id = state.table.cellNames?.LABOR_CODE;
				state.table.rows.forEach((row: any) => {
					row.cells[id] = `\t${row.cells[id].toString().padStart(8, '0')}`;
					const rowData = getColumnsRowMapObject(columnsToCamelCase(state.table.columns), row.cells, ObjectType.columnName, true);
					tableData.push(rowData);
				});
			} else if (state.name === 'NdSkladList') {
				id = state.table.cellNames?.X_ND_NR;
				rebateId = state.table.cellNames?.RABAT_SK;
				state.table.rows.forEach((row: any) => {
					const partLength = row.cells[id].length;
					const rebateidLength = row.cells[rebateId].length;
					row.cells[id] = `\t${row.cells[id].toString().padStart(partLength, '0')}`;
					row.cells[rebateId] = `\t${row.cells[rebateId].toString().padStart(rebateidLength, '0')}`;
					const rowData = getColumnsRowMapObjectForExportFunc(columnsToCamelCase(state.table.columns.filter(x => x.isVisible === true)), row.cells, ObjectType.columnName, true);
					tableData.push(rowData);
				});
			} else {
				const licenseId = state.table.cellNames?.LICENSE_NUMBER;
				const itemId = state.table.cellNames?.ITEM_NO;
				state.table.rows.forEach((row: any) => {
					if (licenseId !== undefined) {
						row.cells[licenseId] = row.cells[licenseId] ? `${row.cells[licenseId].toString()}\t` : row.cells[licenseId];
					}
					if (itemId !== undefined) {
						row.cells[itemId] = row.cells[itemId] ? `${row.cells[itemId].toString()}\t` : row.cells[itemId];
					}
					const rowData = getColumnsRowMapObjectForExportFunc(columnsToCamelCase(state.table.columns.filter(x => x.isVisible === true)), row.cells, ObjectType.columnName, true);
					tableData.push(rowData);
				});
			}
			const formattedObj = tableData.map((row: any) => renameKeysForExportFunc(row));
			const val = localizeFileName(state.crumb.label);
			const url = `data:text/csv;charset=utf-8,%EF%BB%BF${encodeURIComponent(parseObj2Csv(formattedObj))}`;
			return forceFileDownload(url, val);
		},
		[ROW_DETAIL]({ commit, dispatch }: { commit: Commit, dispatch: Dispatch }, data: any) {
			return commitRowDetail(commit, dispatch, apiProvider.rowDetail(data, { cancelTokenKey: ROW_DETAIL, commit }));
		},
	};
}
